import { queryOptions } from '@tanstack/react-query';

import { apiBffFetch } from '../index';

export const PORTFOLIO_QUERY_KEYS = {
  portfolioPerformance: 'portfolio-performance',
  walletPortfolio: 'wallet-portfolio',
  walletSyncStatus: 'wallet-sync-status',
} as const;

export interface PortfolioPerformance {
  period?: '1w' | '1m' | '1y' | 'all';
  numberOfPositions: number;
  totalInvestedInUsd: number | null;
  currentCostBasisInUsd: number | null;
  totalBalanceInUsd: number | null;
  realizedPnlInUsd: number | null;
  unrealizedPnlInUsd: number | null;
  totalPnlInUsd: number | null;
  incomplete: boolean;
  data: Array<{ date: string; value: number }>;
}

interface PortfolioPerformanceParams {
  period: '1w' | '1m' | '1y' | 'all';
  ownerType: 'User' | 'Entity';
  ownerId?: string;
  position?: string;
}

export const getPortfolioPerformanceOptions = (params: PortfolioPerformanceParams) => {
  const queryParams = {
    period: params.period,
    ownerType: params.ownerType,
    ...(params.ownerId ? { ownerId: params.ownerId } : {}),
    ...(params.position ? { position: params.position } : {}),
  };

  return queryOptions({
    queryKey: [
      PORTFOLIO_QUERY_KEYS.portfolioPerformance,
      params.period,
      params.ownerType,
      params.ownerId,
      params.position,
    ],
    queryFn: () =>
      apiBffFetch<PortfolioPerformance>('/portfolio-views/performance', { params: queryParams }),
    refetchOnMount: true,
    retry: 1,
  });
};

export enum WalletViewState {
  InitialIndexing = 'InitialIndexing',
  Refreshing = 'Refreshing',
  CaughtUp = 'CaughtUp',
  Partial = 'Partial',
  RefreshFailed = 'RefreshFailed',
  InitialFailed = 'InitialFailed',
}

export enum WalletSyncKind {
  Balances = 'balances',
  History = 'history',
}

export enum WalletSyncRunStatus {
  Queued = 'queued',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
}

export interface WalletViewAsset {
  id: string;
  coinType: string;
  symbol: string;
  decimals: number;
  iconUrl: string | null;
}

export interface WalletViewBalance {
  asset: WalletViewAsset;
  amount: string;
  currentPriceInUsd: string | null;
}

export interface WalletViewPosition {
  id: string;
  assetId: string;
  costBasisInUsd: string | null;
  currentValueInUsd: string | null;
}

export interface WalletViewSyncRun {
  runId: string;
  kind: WalletSyncKind;
  status: WalletSyncRunStatus;
  requestedAt: string;
}

export interface WalletSyncStatus {
  state: WalletViewState;
  balances: WalletViewSyncRun | null;
  history: WalletViewSyncRun | null;
}

export interface WalletPortfolio {
  wallet: { address: string };
  balances: WalletViewBalance[];
  positions: WalletViewPosition[];
  freshness: WalletSyncStatus;
}

const WALLET_SYNC_POLL_INTERVAL_MS = 5_000;

const walletSyncNeedsPolling = (status: WalletSyncStatus | undefined) => {
  if (!status) {
    return false;
  }

  const activeRun = [status.balances, status.history].some(
    (run) =>
      run?.status === WalletSyncRunStatus.Queued || run?.status === WalletSyncRunStatus.Running,
  );
  const stateStillIndexing =
    status.state === WalletViewState.InitialIndexing ||
    status.state === WalletViewState.Refreshing ||
    status.state === WalletViewState.Partial;

  return activeRun || stateStillIndexing;
};

export const getWalletPortfolioOptions = () =>
  queryOptions({
    queryKey: [PORTFOLIO_QUERY_KEYS.walletPortfolio],
    queryFn: () => apiBffFetch<WalletPortfolio>('/wallet-views/portfolio'),
    staleTime: 30_000,
    refetchInterval: ({ state }) =>
      walletSyncNeedsPolling(state.data?.freshness) ? WALLET_SYNC_POLL_INTERVAL_MS : false,
  });

export const getWalletSyncStatusOptions = () =>
  queryOptions({
    queryKey: [PORTFOLIO_QUERY_KEYS.walletSyncStatus],
    queryFn: () => apiBffFetch<WalletSyncStatus>('/wallet-views/sync-status'),
    staleTime: 30_000,
    refetchInterval: ({ state }) =>
      walletSyncNeedsPolling(state.data) ? WALLET_SYNC_POLL_INTERVAL_MS : false,
  });
